import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Link as LinkIcon, Mail, FileText, Search, User, MapPin, Calendar, Hash, TrendingUp, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { formatNumberWithCommas } from "@/lib/utils";

interface EnrichedData {
  domain?: string;
  company_name?: string;
  linkedin_url?: string;
  email_domain?: string;
  industry?: string;
  bizraport_data?: {
    krs?: string;
    nip?: string;
    regon?: string;
    company_name?: string;
    address?: string;
    registration_date?: string;
    financial_summary?: string;
    legal_form?: string;
    ownership_form?: string;
    registry_court?: string;
    pkd_codes?: string[];
    wojewodztwo?: string;
  };
  debug_info?: {
    linkedin_person_found?: boolean;
    linkedin_person_score?: number;
    phantombuster_enabled?: boolean;
    linkedin_company_found?: boolean;
    linkedin_company_score?: number;
    person_linkedin_method?: string;
    top_company_candidates?: any[];
    company_linkedin_method?: string;
    linkedin_person_ai_used?: boolean;
    linkedin_company_ai_used?: boolean;
    person_data_quality_score?: number | null;
    phantombuster_person_used?: boolean;
    company_data_quality_score?: number | null;
    linkedin_person_candidates?: number;
    phantombuster_company_used?: boolean;
    linkedin_company_candidates?: number;
  };
  person_data?: {
    name?: string;
    headline?: string;
    location?: string;
    linkedin_url?: string;
    experience?: any[];
    education?: any[];
  };
}

interface QuizEnrichedDataViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enrichedData: EnrichedData | null;
  domain: string;
  quizAnswers?: any;
}

export function QuizEnrichedDataViewer({ 
  open, 
  onOpenChange, 
  enrichedData,
  domain,
  quizAnswers
}: QuizEnrichedDataViewerProps) {
  if (!enrichedData) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dane wzbogacone - {domain}</DialogTitle>
          </DialogHeader>
          <div className="text-center py-8 text-muted-foreground">
            Brak danych wzbogaconych
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const normalizedData = (enrichedData as any)?.enrichedData || enrichedData || {};
  const companyData = normalizedData;
  const bizData = normalizedData.bizraport_data;
  const debugInfo = normalizedData.debug_info;
  const personData = normalizedData.person_data;
  
  // Extract LinkedIn URLs from multiple possible sources
  const companyLinkedInUrl = companyData.linkedin_url || 
    quizAnswers?.found_linkedin_profile?._raw?.url || 
    quizAnswers?.found_linkedin_profile?.url;
    
  const personLinkedInUrl = personData?.linkedin_url || 
    companyData.person_linkedin;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Dane wzbogacone - {domain}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Quick Action Links */}
          <div className="flex flex-wrap gap-3">
            {/* Bizraport Link */}
            {(bizData?.nip || companyData.bizraport_url) && (
              <a 
                href={companyData.bizraport_url || `https://www.bizraport.pl/firma/${bizData.nip}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors text-sm"
              >
                <FileText className="w-4 h-4" />
                Bizraport.pl
              </a>
            )}
            
            {/* LinkedIn Company Link */}
            {companyLinkedInUrl && (
              <a 
                href={companyLinkedInUrl}
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A66C2] hover:bg-[#004182] text-white rounded-lg font-semibold transition-colors text-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
                LinkedIn Firmy
              </a>
            )}
            
            {/* LinkedIn Person Link */}
            {personLinkedInUrl && (
              <a 
                href={personLinkedInUrl}
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A66C2] hover:bg-[#004182] text-white rounded-lg font-semibold transition-colors text-sm"
              >
                <User className="w-4 h-4" />
                LinkedIn Osoby
              </a>
            )}
          </div>

          {/* Company Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Podstawowe dane firmy
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {companyData.company_name && (
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Nazwa firmy:</span>
                  <p className="text-base font-semibold">{companyData.company_name}</p>
                </div>
              )}
              
              {companyData.email_domain && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{companyData.email_domain}</span>
                </div>
              )}
              
              {companyData.linkedin_url && (
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(companyData.linkedin_url, '_blank')}
                    className="w-full justify-start"
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Profil LinkedIn firmy
                  </Button>
                </div>
              )}

              {companyData.industry && (
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{companyData.industry}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bizraport Data */}
          {bizData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Dane rejestrowe (Bizraport)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {bizData.krs && (
                    <div>
                      <span className="text-xs text-muted-foreground">KRS:</span>
                      <p className="text-sm font-mono font-semibold">{bizData.krs}</p>
                    </div>
                  )}
                  
                  {bizData.nip && (
                    <div>
                      <span className="text-xs text-muted-foreground">NIP:</span>
                      <p className="text-sm font-mono font-semibold">{bizData.nip}</p>
                    </div>
                  )}
                  
                  {bizData.regon && (
                    <div>
                      <span className="text-xs text-muted-foreground">REGON:</span>
                      <p className="text-sm font-mono font-semibold">{bizData.regon}</p>
                    </div>
                  )}
                  
                  {bizData.registration_date && (
                    <div>
                      <span className="text-xs text-muted-foreground">Data rejestracji:</span>
                      <p className="text-sm">{bizData.registration_date}</p>
                    </div>
                  )}
                </div>

                {bizData.address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span className="text-sm">{bizData.address}</span>
                  </div>
                )}

                {(bizData.legal_form || bizData.ownership_form) && (
                  <div className="space-y-2 pt-2 border-t">
                    {bizData.legal_form && (
                      <div>
                        <span className="text-xs text-muted-foreground">Forma prawna:</span>
                        <p className="text-sm">{bizData.legal_form}</p>
                      </div>
                    )}
                    {bizData.ownership_form && (
                      <div>
                        <span className="text-xs text-muted-foreground">Forma własności:</span>
                        <p className="text-sm">{bizData.ownership_form}</p>
                      </div>
                    )}
                  </div>
                )}

                {bizData.financial_summary && (() => {
                  const financialData = bizData.financial_summary.split('; ').reduce((acc: any, item: string) => {
                    const [label, value] = item.split(': ');
                    acc[label] = value;
                    return acc;
                  }, {});
                  
                  const cardConfigs = [
                    { 
                      label: 'Aktywa', 
                      value: financialData['Aktywa'], 
                      icon: '📊',
                      bgGradient: 'from-blue-50 to-blue-100',
                      borderColor: 'border-blue-300',
                      textColor: 'text-blue-700',
                      valueColor: 'text-blue-900'
                    },
                    { 
                      label: 'Przychód', 
                      value: financialData['Przychody'] || financialData['Przychód'], 
                      icon: '💰',
                      bgGradient: 'from-green-50 to-green-100',
                      borderColor: 'border-green-300',
                      textColor: 'text-green-700',
                      valueColor: 'text-green-900'
                    },
                    { 
                      label: 'Zysk', 
                      value: financialData['Zysk'], 
                      icon: '📈',
                      bgGradient: 'from-orange-50 to-orange-100',
                      borderColor: 'border-orange-300',
                      textColor: 'text-orange-700',
                      valueColor: 'text-orange-900'
                    },
                    { 
                      label: 'Wartość firmy', 
                      value: financialData['Wartość firmy'], 
                      icon: '💎',
                      bgGradient: 'from-purple-50 to-purple-100',
                      borderColor: 'border-purple-300',
                      textColor: 'text-purple-700',
                      valueColor: 'text-purple-900'
                    }
                  ].filter(card => card.value);

                  return cardConfigs.length > 0 ? (
                    <div className="pt-2 border-t">
                      <span className="text-xs text-muted-foreground mb-3 block font-semibold">Dane finansowe</span>
                      <div className="grid grid-cols-2 gap-3">
                        {cardConfigs.map((card, idx) => (
                          <div 
                            key={idx}
                            className={`bg-gradient-to-br ${card.bgGradient} p-3 rounded-lg border-2 ${card.borderColor} shadow-sm`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">{card.icon}</span>
                              <div className={`text-xs font-semibold ${card.textColor}`}>{card.label}</div>
                            </div>
                            <div className={`text-lg font-bold ${card.valueColor}`}>
                              {formatNumberWithCommas(card.value)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}

                {bizData.pkd_codes && bizData.pkd_codes.length > 0 && (
                  <div className="pt-2 border-t">
                    <span className="text-xs text-muted-foreground mb-1 block">Kody PKD:</span>
                    <div className="flex flex-wrap gap-1">
                      {bizData.pkd_codes.map((code, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {code}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Person Data */}
          {personData && personData.name && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Dane osoby kontaktowej
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Imię i nazwisko:</span>
                  <p className="text-base font-semibold">{personData.name}</p>
                </div>

                {personData.headline && (
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Stanowisko:</span>
                    <p className="text-sm">{personData.headline}</p>
                  </div>
                )}

                {personData.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{personData.location}</span>
                  </div>
                )}

                {personData.linkedin_url && (
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(personData.linkedin_url, '_blank')}
                      className="w-full justify-start"
                    >
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Profil LinkedIn osoby
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Debug Info */}
          {debugInfo && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Informacje techniczne
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Company Search Info */}
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Wyszukiwanie firmy:</h4>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant={debugInfo.linkedin_company_found ? "default" : "secondary"}>
                      {debugInfo.linkedin_company_found ? "✓" : "✗"} LinkedIn znaleziony
                    </Badge>
                    {debugInfo.linkedin_company_score !== undefined && (
                      <Badge variant="outline">
                        Score: {debugInfo.linkedin_company_score}
                      </Badge>
                    )}
                    {debugInfo.company_linkedin_method && (
                      <Badge variant="outline">
                        Metoda: {debugInfo.company_linkedin_method}
                      </Badge>
                    )}
                    {debugInfo.linkedin_company_candidates !== undefined && (
                      <Badge variant="outline">
                        Kandydatów: {debugInfo.linkedin_company_candidates}
                      </Badge>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Person Search Info */}
                {debugInfo.linkedin_person_found !== undefined && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Wyszukiwanie osoby:</h4>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant={debugInfo.linkedin_person_found ? "default" : "secondary"}>
                        {debugInfo.linkedin_person_found ? "✓" : "✗"} Osoba znaleziona
                      </Badge>
                      {debugInfo.linkedin_person_score !== undefined && (
                        <Badge variant="outline">
                          Score: {debugInfo.linkedin_person_score}
                        </Badge>
                      )}
                      {debugInfo.person_linkedin_method && (
                        <Badge variant="outline">
                          Metoda: {debugInfo.person_linkedin_method}
                        </Badge>
                      )}
                      {debugInfo.linkedin_person_candidates !== undefined && (
                        <Badge variant="outline">
                          Kandydatów: {debugInfo.linkedin_person_candidates}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                <Separator />

                {/* Additional Debug Info */}
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Dodatkowe:</h4>
                  <div className="flex flex-wrap gap-2">
                    {debugInfo.phantombuster_enabled && (
                      <Badge variant="outline">
                        PhantomBuster włączony
                      </Badge>
                    )}
                    {debugInfo.linkedin_company_ai_used && (
                      <Badge variant="outline">
                        AI użyte (firma)
                      </Badge>
                    )}
                    {debugInfo.linkedin_person_ai_used && (
                      <Badge variant="outline">
                        AI użyte (osoba)
                      </Badge>
                    )}
                    {debugInfo.company_data_quality_score !== null && debugInfo.company_data_quality_score !== undefined && (
                      <Badge variant="outline">
                        Jakość danych firmy: {debugInfo.company_data_quality_score}
                      </Badge>
                    )}
                    {debugInfo.person_data_quality_score !== null && debugInfo.person_data_quality_score !== undefined && (
                      <Badge variant="outline">
                        Jakość danych osoby: {debugInfo.person_data_quality_score}
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
